import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { PaperPlaneTilt, Sparkle, User as UserIcon } from "@phosphor-icons/react";

const STARTERS = [
    "How long does clear aligner treatment take?",
    "How do I clean my aligners?",
    "Are there any food restrictions?",
    "How much will it cost?",
];

export default function PatientChat() {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const scrollRef = useRef(null);

    useEffect(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }, [messages]);

    const send = async (text) => {
        const message = text || input;
        if (!message.trim() || loading) return;
        const userMsgId = `u-${Date.now()}`;
        setMessages((m) => [...m, { id: userMsgId, role: "user", content: message }]);
        setInput("");
        setLoading(true);
        try {
            const { data } = await api.post("/chat/send", { message, session_id: sessionId });
            setSessionId(data.session_id);
            setMessages((m) => [...m, { id: `a-${Date.now()}`, role: "assistant", content: data.reply }]);
        } catch {
            toast.error("Chat failed");
        }
        setLoading(false);
    };

    return (
        <div className="flex flex-col h-[calc(100vh-12rem)]" data-testid="patient-chat-page">
            <div className="mb-5">
                <div className="overline text-primary mb-2">Powered by Claude Sonnet 4.5</div>
                <h1 className="font-display text-3xl font-bold">Smile Assistant</h1>
                <p className="text-muted-foreground mt-1">Ask anything about clear aligners, treatment, or oral care.</p>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto bg-card border border-border rounded-2xl p-5 space-y-4" data-testid="chat-messages">
                {messages.length === 0 && (
                    <div className="py-8 text-center">
                        <div className="inline-flex w-14 h-14 rounded-2xl bg-primary/10 items-center justify-center mb-4">
                            <Sparkle size={26} weight="duotone" color="hsl(192 100% 31%)" />
                        </div>
                        <p className="font-display font-semibold mb-6">How can I help with your smile journey?</p>
                        <div className="grid sm:grid-cols-2 gap-2 max-w-md mx-auto">
                            {STARTERS.map((s) => (
                                <button
                                    key={s}
                                    onClick={() => send(s)}
                                    className="p-3 text-left text-sm border border-border rounded-xl hover:border-primary hover:bg-primary/5 transition-colors"
                                    data-testid={`starter-${s.slice(0, 10)}`}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                {messages.map((m) => (
                    <div key={m.id} className={`flex gap-3 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                        {m.role === "assistant" && (
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                <Sparkle size={16} weight="duotone" color="hsl(192 100% 31%)" />
                            </div>
                        )}
                        <div className={`max-w-[75%] rounded-2xl px-4 py-3 text-sm whitespace-pre-wrap ${
                            m.role === "user" ? "bg-primary text-white" : "bg-secondary"
                        }`}>
                            {m.content}
                        </div>
                        {m.role === "user" && (
                            <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center shrink-0">
                                <UserIcon size={16} />
                            </div>
                        )}
                    </div>
                ))}
                {loading && (
                    <div className="flex gap-2 items-center text-muted-foreground text-sm">
                        <div className="flex gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-bounce" style={{ animationDelay: "0ms" }} />
                            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-bounce" style={{ animationDelay: "150ms" }} />
                            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-bounce" style={{ animationDelay: "300ms" }} />
                        </div>
                        Thinking...
                    </div>
                )}
            </div>

            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="mt-4 flex gap-2" data-testid="chat-form">
                <Input
                    placeholder="Ask anything..."
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    disabled={loading}
                    className="h-12 rounded-full"
                    data-testid="chat-message-input"
                />
                <Button type="submit" disabled={loading || !input.trim()} className="h-12 w-12 rounded-full p-0" data-testid="chat-send-message-btn">
                    <PaperPlaneTilt size={18} weight="fill" />
                </Button>
            </form>
        </div>
    );
}
